import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { 
    ArrowLeft, UserPlus, FileText, Download, Loader2, 
    CheckCircle2, Clock, AlertCircle, Stethoscope
} from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const specialistTypes = [
    { id: 'cardiologist', name: 'Cardiologist', description: 'Heart and cardiovascular system' },
    { id: 'dermatologist', name: 'Dermatologist', description: 'Skin conditions' },
    { id: 'neurologist', name: 'Neurologist', description: 'Brain and nervous system' },
    { id: 'orthopedic', name: 'Orthopedic Surgeon', description: 'Bones and joints' },
    { id: 'gastroenterologist', name: 'Gastroenterologist', description: 'Digestive system' },
    { id: 'pulmonologist', name: 'Pulmonologist', description: 'Lungs and respiratory' },
    { id: 'endocrinologist', name: 'Endocrinologist', description: 'Hormones and metabolism' },
    { id: 'rheumatologist', name: 'Rheumatologist', description: 'Autoimmune and joint diseases' },
    { id: 'psychiatrist', name: 'Psychiatrist', description: 'Mental health' },
    { id: 'ophthalmologist', name: 'Ophthalmologist', description: 'Eye conditions' },
    { id: 'ent', name: 'ENT Specialist', description: 'Ear, nose, and throat' },
    { id: 'urologist', name: 'Urologist', description: 'Urinary system' },
    { id: 'oncologist', name: 'Oncologist', description: 'Cancer treatment' },
    { id: 'other', name: 'Other Specialist', description: 'Other specialization' }
];

const statusColors = {
    pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    approved: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    completed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
};

export default function Referrals() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [referrals, setReferrals] = useState([]);
    const [showRequest, setShowRequest] = useState(false);
    const [formData, setFormData] = useState({
        specialist_type: '',
        reason: '',
        symptoms: '',
        urgency: 'routine'
    });

    useEffect(() => {
        fetchReferrals();
    }, []);

    const fetchReferrals = async () => {
        try {
            const response = await axios.get(`${API}/referrals`);
            setReferrals(response.data);
        } catch (err) {
            console.error('Failed to fetch referrals');
        }
    };

    const handleSubmit = async () => {
        if (!formData.specialist_type || !formData.reason) {
            toast.error('Please fill in all required fields');
            return;
        }

        setLoading(true);
        try {
            await axios.post(`${API}/referrals/request`, formData);
            toast.success('Referral request submitted successfully!');
            setShowRequest(false);
            setFormData({ specialist_type: '', reason: '', symptoms: '', urgency: 'routine' });
            fetchReferrals();
        } catch (err) {
            if (err.response?.status === 403) {
                toast.error('Premium subscription required for referrals');
                navigate('/subscription');
            } else {
                toast.error('Failed to submit referral request');
            }
        } finally {
            setLoading(false);
        }
    };

    const downloadReferralLetter = (referral) => {
        const doc = new jsPDF();
        const specialist = specialistTypes.find(s => s.id === referral.specialist_type);
        
        // Header
        doc.setFontSize(20);
        doc.setTextColor(20, 184, 166);
        doc.text('MediGuide', 20, 20);
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text('AI-Powered Health Assessment', 20, 27);
        
        // Title
        doc.setFontSize(16);
        doc.setTextColor(0);
        doc.text('Specialist Referral Letter', 20, 45);
        
        // Line
        doc.setDrawColor(20, 184, 166);
        doc.line(20, 50, 190, 50);
        
        // Content
        doc.setFontSize(11);
        doc.text(`Date: ${new Date(referral.created_at).toLocaleDateString()}`, 20, 60);
        doc.text(`Referral ID: ${referral.id}`, 20, 68);
        
        doc.setFontSize(12);
        doc.text('To Whom It May Concern,', 20, 85);
        
        doc.setFontSize(11);
        const bodyText = `This letter serves as a referral for the patient to see a ${specialist?.name || referral.specialist_type}. Based on the patient's reported symptoms and our AI-assisted assessment, we recommend specialist consultation for further evaluation and treatment.`;
        const splitBody = doc.splitTextToSize(bodyText, 170);
        doc.text(splitBody, 20, 95);
        
        doc.text('Reason for Referral:', 20, 120);
        const reasonSplit = doc.splitTextToSize(referral.reason, 170);
        doc.text(reasonSplit, 20, 128);
        
        if (referral.symptoms) {
            doc.text('Reported Symptoms:', 20, 150);
            const symptomsSplit = doc.splitTextToSize(referral.symptoms, 170);
            doc.text(symptomsSplit, 20, 158);
        }
        
        doc.text(`Urgency Level: ${referral.urgency.charAt(0).toUpperCase() + referral.urgency.slice(1)}`, 20, 180);
        doc.text(`Status: ${referral.status.charAt(0).toUpperCase() + referral.status.slice(1)}`, 20, 188);
        
        // Footer
        doc.setFontSize(9);
        doc.setTextColor(100);
        doc.text('This referral was generated through MediGuide AI Health Assessment platform.', 20, 250);
        doc.text('Please present this letter to the specialist during your appointment.', 20, 256);
        doc.text('For verification, contact: support@mediguide.app', 20, 262);
        
        doc.save(`MediGuide_Referral_${referral.id}.pdf`);
        toast.success('Referral letter downloaded');
    };

    return (
        <div className="min-h-screen bg-background py-8 lg:py-12" data-testid="referrals-page">
            <div className="max-w-4xl mx-auto px-4">
                <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
                </Button>
                
                {/* Centered Header Section */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                            <UserPlus className="w-5 h-5 text-primary" />
                        </div>
                        <h1 className="font-heading text-3xl font-bold">Specialist Referrals</h1>
                    </div>
                    <p className="text-muted-foreground mb-4">Request and manage referrals to specialists</p>
                    <Button onClick={() => setShowRequest(true)} className="rounded-full">
                        <UserPlus className="w-4 h-4 mr-2" /> Request Referral
                    </Button>
                </div>

                {showRequest && (
                    <Card className="mb-8">
                        <CardHeader>
                            <CardTitle>Request Specialist Referral</CardTitle>
                            <CardDescription>Fill in the details below to request a referral</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <Label>Specialist Type *</Label>
                                        <Select 
                                            value={formData.specialist_type} 
                                            onValueChange={(v) => setFormData({...formData, specialist_type: v})}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select specialist" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {specialistTypes.map(s => (
                                                    <SelectItem key={s.id} value={s.id}>
                                                        <div className="flex flex-col">
                                                            <span>{s.name}</span>
                                                            <span className="text-xs text-muted-foreground">{s.description}</span>
                                                        </div>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Urgency Level</Label>
                                        <Select 
                                            value={formData.urgency} 
                                            onValueChange={(v) => setFormData({...formData, urgency: v})}
                                        >
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="routine">Routine (2-4 weeks)</SelectItem>
                                                <SelectItem value="soon">Soon (1-2 weeks)</SelectItem>
                                                <SelectItem value="urgent">Urgent (Within a week)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <Label>Reason for Referral *</Label>
                                        <Textarea 
                                            value={formData.reason} 
                                            onChange={(e) => setFormData({...formData, reason: e.target.value})}
                                            placeholder="Explain why you need to see this specialist..."
                                            rows={3}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Current Symptoms</Label>
                                        <Textarea 
                                            value={formData.symptoms} 
                                            onChange={(e) => setFormData({...formData, symptoms: e.target.value})}
                                            placeholder="Describe your current symptoms..."
                                            rows={3}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-3 justify-end">
                                <Button variant="outline" onClick={() => setShowRequest(false)}>Cancel</Button>
                                <Button onClick={handleSubmit} disabled={loading} className="rounded-full">
                                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                                    Submit Request
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                <Card>
                    <CardHeader>
                        <CardTitle>Your Referrals</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {referrals.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                                <Stethoscope className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>No referrals requested yet</p>
                                <Button 
                                    variant="outline" 
                                    className="mt-4"
                                    onClick={() => setShowRequest(true)}
                                >
                                    Request Your First Referral
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {referrals.map((r) => {
                                    const specialist = specialistTypes.find(s => s.id === r.specialist_type);
                                    return (
                                        <div key={r.id} className="p-4 border rounded-xl">
                                            <div className="flex items-start justify-between">
                                                <div className="flex items-start gap-4">
                                                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                                        <UserPlus className="w-5 h-5 text-primary" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium">{specialist?.name || r.specialist_type}</p>
                                                        <p className="text-sm text-muted-foreground mt-1">{r.reason}</p>
                                                        <div className="flex items-center gap-3 mt-2">
                                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[r.status]}`}>
                                                                {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                                                            </span>
                                                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                                                                <Clock className="w-3 h-3" />
                                                                {new Date(r.created_at).toLocaleDateString()}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <Button 
                                                    variant="outline" 
                                                    size="sm"
                                                    onClick={() => downloadReferralLetter(r)}
                                                >
                                                    <Download className="w-4 h-4 mr-1" /> Download
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
